import { registerBatchTranslations } from './WithcookieSlice';

/**
 * 번역 배치 처리 매니저
 * 여러 개의 번역 요청을 모아서 배치로 처리합니다.
 */
class TranslationBatchManager {
  constructor() {
    this.dispatch = null;
    this.pendingWords = [];
    this.processedWords = new Set(); // 이미 처리한 단어들
    this.failedWords = new Set(); // 실패한 단어들 (재시도용)
    this.processingWords = new Set(); // 현재 처리 중인 단어들
    this.maxBatchSize = 50; // 10개씩 모아서 보내기
    this.isProcessing = false; // 현재 처리 중인지 여부
    this.flushTimer = null; // 지연된 flush를 위한 타이머
    this.maxRetries = 3; // 최대 재시도 횟수
    this.retryAttempts = new Map(); // 단어별 재시도 횟수 추적
    
  }

  /**
   * dispatch 설정
   * @param {Function} dispatch - Redux dispatch 함수
   */
  setDispatch(dispatch) {
    this.dispatch = dispatch;
  }

  /**
   * 새로운 단어를 배치에 추가
   * @param {string} word - 추가할 단어
   */
  addWord(word) {
    if (!word || typeof word !== 'string') {
      return;
    }

    // 이미 처리한 단어인지 확인
    if (this.processedWords.has(word)) {
      return;
    }

    // 현재 처리 중인 단어인지 확인
    if (this.processingWords.has(word)) {
      return;
    }

    // 현재 대기 중인 단어인지 확인
    if (this.pendingWords.includes(word)) {
      return;
    }

    // 배열에 추가
    this.pendingWords.push(word);

    // 10개가 모이면 즉시 전송
    if (this.pendingWords.length >= this.maxBatchSize) {
      // 기존 타이머 취소 (중복 처리 방지)
      this.cancelDelayedFlush();
      this.flush();
    } else {
      // 10개가 안 되면 3초 후에 자동으로 flush (남은 단어들도 처리되도록)
      this.scheduleDelayedFlush();
    }
  }

  /**
   * 지연된 flush 스케줄링
   */
  scheduleDelayedFlush() {
    // 기존 타이머가 있으면 취소
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
    }
    
    // 3초 후에 flush 실행
    this.flushTimer = setTimeout(() => {
      if (this.pendingWords.length > 0 && !this.isProcessing) {
        this.flushWithSizeLimit();
      }
    }, 3000);
  }

  /**
   * 지연된 flush 타이머 취소
   */
  cancelDelayedFlush() {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
  }

  /**
   * 배치 크기 제한이 적용된 flush
   */
  async flushWithSizeLimit() {
    // 이미 처리 중이면 대기
    if (this.isProcessing) {
      return;
    }

    if (this.pendingWords.length === 0) {
      return;
    }

    // 배치 크기 제한 적용
    const wordsToProcess = this.pendingWords.slice(0, this.maxBatchSize);
    const remainingWords = this.pendingWords.slice(this.maxBatchSize);
    
    // 남은 단어들은 대기열에 유지
    this.pendingWords = remainingWords;
    

    // 제한된 크기로 flush 실행
    await this.processWordBatch(wordsToProcess);
    
    // 남은 단어가 있으면 다시 스케줄링
    if (this.pendingWords.length > 0) {
      this.scheduleDelayedFlush();
    }
  }

  /**
   * 현재 대기 중인 단어들을 배치로 처리
   */
  async flush() {
    // 이미 처리 중이면 대기
    if (this.isProcessing) {
      return;
    }

    if (this.pendingWords.length === 0) {
      return;
    }

    // 기존 타이머 취소
    this.cancelDelayedFlush();

    // 현재 대기 중인 단어들 복사
    const words = [...this.pendingWords];

    // 대기열 초기화
    this.pendingWords = [];

    // 단어 배치 처리
    await this.processWordBatch(words);
  }

  /**
   * 단어 배치 처리 (공통 로직)
   * @param {string[]} words - 처리할 단어들
   */
  async processWordBatch(words) {
    if (words.length === 0) {
      return;
    }


    // 처리 중 플래그 설정
    this.isProcessing = true;

    // 처리 중인 단어로 추가
    words.forEach(word => {
      this.processingWords.add(word);
    });

    try {
      if (!this.dispatch) {
        console.error('[TranslationBatchManager] dispatch가 설정되지 않음');
        // 실패한 단어들을 다시 대기열에 추가
        words.forEach(word => {
          this.processingWords.delete(word);
          if (!this.pendingWords.includes(word)) {
            this.pendingWords.push(word);
          }
        });
        return;
      }
      

      // Redux 액션 디스패치
      const result = await this.dispatch(registerBatchTranslations(words));
      

      if (registerBatchTranslations.fulfilled.match(result)) {
        // 성공한 단어들을 처리 완료 목록에 추가
        words.forEach(word => {
          this.processedWords.add(word);
          this.processingWords.delete(word);
          this.failedWords.delete(word); // 실패 목록에서도 제거
          this.retryAttempts.delete(word); // 재시도 횟수 초기화
        });
      } else {
        console.error('[번역 배치] ❌ 처리 실패:', result.error);
        // 실패한 단어들 처리
        this.handleFailedWords(words);
      }
    } catch (error) {
      console.error('[번역 배치] ❌ 처리 중 오류:', error);
      // 실패한 단어들 처리
      this.handleFailedWords(words);
    } finally {
      // 처리 중 플래그 해제
      this.isProcessing = false;

      // 대기 중인 단어가 있으면 계속 처리
      if (this.pendingWords.length > 0) {
        // 짧은 지연 후 다음 배치 처리
        setTimeout(() => {
          if (this.pendingWords.length > 0 && !this.isProcessing) {
            this.flush();
          }
        }, 500);
      }
    }
  }

  /**
   * 실패한 단어들 처리 (재시도 로직)
   * @param {string[]} words - 실패한 단어들
   */
  handleFailedWords(words) {
    words.forEach(word => {
      this.processingWords.delete(word);
      
      // 재시도 횟수 확인
      const currentRetries = this.retryAttempts.get(word) || 0;
      
      if (currentRetries < this.maxRetries) {
        // 재시도 가능한 경우
        this.retryAttempts.set(word, currentRetries + 1);
        this.failedWords.add(word);
        
        // 5초 후에 재시도
        setTimeout(() => {
          if (this.failedWords.has(word) && !this.pendingWords.includes(word) && !this.processingWords.has(word)) {
            this.failedWords.delete(word);
            this.pendingWords.push(word);
            this.scheduleDelayedFlush();
          }
        }, 5000);
      } else {
        // 최대 재시도 횟수 초과
        console.error(`[번역 배치] "${word}" 최대 재시도 횟수 초과, 포기`);
        this.failedWords.add(word);
        this.retryAttempts.delete(word);
      }
    });
  }

  /**
   * 매니저 정리 (컴포넌트 언마운트 시 호출)
   */
  cleanup() {
    // 타이머 정리
    this.cancelDelayedFlush();

    // 남은 단어가 있으면 처리
    if (this.pendingWords.length > 0) {
      this.flush();
    }
  }

  /**
   * 매니저 상태 초기화 (디버깅용)
   */
  reset() {
    this.cancelDelayedFlush();
    this.pendingWords = [];
    this.processingWords.clear();
    this.isProcessing = false;
  }

  /**
   * 현재 상태 로그 출력
   */

  /**
   * 현재 상태 정보 반환
   */
  getStatus() {
    return {
      pending: this.pendingWords.length,
      processing: this.processingWords.size,
      processed: this.processedWords.size,
      failed: this.failedWords.size,
      isProcessing: this.isProcessing
    };
  }
}

export default TranslationBatchManager;